/**
 * Build the intake-summary markdown sent to the backend's /intake-export
 * endpoint (which wraps it with a cover + runs pandoc → .docx for client
 * review). The frontend owns the field labels + value shapes, so the Q&A
 * rendering lives here rather than in the label-less backend schema.
 */
import type { AssessmentState } from '../api/client'
import { isFilled } from '../schema/intakeCompletion'
import { SECTIONS, type ChoiceOption, type FieldDef } from '../schema/intakeSchema'

function optionLabel(options: ChoiceOption[], value: string): string {
  return options.find((o) => o.value === value)?.label ?? value
}

/** Render one field's captured value as a markdown fragment, or null to skip. */
function formatField(field: FieldDef, value: unknown): string | null {
  if (!isFilled(value)) return null
  const t = field.type
  switch (t.kind) {
    case 'text':
    case 'textarea':
    case 'number':
      return String(value)
    case 'radio':
      return optionLabel(t.options, String(value))
    case 'multiselect': {
      const arr = Array.isArray(value) ? value : [value]
      return arr.map((v) => `- ${optionLabel(t.options, String(v))}`).join('\n')
    }
    case 'list': {
      const arr = Array.isArray(value) ? value : String(value).split('\n')
      const items = arr.map((s) => String(s).trim()).filter(Boolean)
      return items.length ? items.map((s) => `- ${s}`).join('\n') : null
    }
    case 'eligibility-grid': {
      const v = value as Record<string, { result?: string; notes?: string }>
      const lines = t.rows
        .map((r) => {
          const row = v[r.value]
          if (!row || (row.result === undefined && !row.notes)) return null
          const mark = row.result === 'Pass' ? 'Pass' : '—'
          const notes = row.notes ? ` (${row.notes})` : ''
          return `- **${r.label}:** ${mark}${notes}`
        })
        .filter(Boolean)
      return lines.length ? lines.join('\n') : null
    }
    case 'use-cases-table': {
      // captured_fields is `additionalProperties: true`, so an imported /
      // round-tripped value can be any shape, not just the expected array —
      // guard before .filter so a malformed shape skips rather than throwing.
      if (!Array.isArray(value)) return null
      const arr = value as Array<{ name?: string; description?: string; type?: string; impact?: string }>
      const lines = arr
        .filter((r) => r && (r.name || r.description))
        .map((r) => {
          const bits = [
            r.name && `**${r.name}**`,
            r.description,
            r.type && `type: ${r.type}`,
            r.impact && `impact: ${r.impact}`,
          ].filter(Boolean)
          return `- ${bits.join(' — ')}`
        })
      return lines.length ? lines.join('\n') : null
    }
    case 'qa-list': {
      // Same defensive guard as use-cases-table: qa_entries can arrive as a
      // non-array from an import / round-trip; skip rather than crash.
      if (!Array.isArray(value)) return null
      const arr = value as Array<{ question?: string; answer?: string }>
      const blocks = arr
        .filter((r) => r && (r.question || r.answer))
        .map((r) => `**Q:** ${r.question ?? ''}\n\n**A:** ${r.answer ?? ''}`)
      return blocks.length ? blocks.join('\n\n') : null
    }
    case 'contacts-table': {
      if (!Array.isArray(value)) return null
      const arr = value as Array<{ name?: string; title?: string; contact_type?: string; email?: string; phone?: string }>
      const lines = arr
        .filter((r) => r && (r.name || r.email))
        .map((r) => {
          const bits = [
            r.name && `**${r.name}**`,
            r.title,
            r.contact_type && `(${r.contact_type})`,
            r.email,
            r.phone,
          ].filter(Boolean)
          return `- ${bits.join(' — ')}`
        })
      return lines.length ? lines.join('\n') : null
    }
    default:
      return null
  }
}

/** Max characters for the one-line example pulled from a field's `help`. */
const EXAMPLE_MAX = 200
/** The hint is cut to this many sentences, so a customer-facing questionnaire
 *  stays readable (some hints run to a full consultant-facing paragraph). */
const HINT_SENTENCES = 2

/** Abbreviations whose period never ends a sentence. */
const ABBREVIATIONS = /\b(e\.g|i\.e|etc|vs|approx|incl)\./gi

/** The field's hint, trimmed to its first sentences. A sentence ends at
 *  . ! or ? followed by whitespace — except after a known abbreviation
 *  ("e.g. …"), which is masked before splitting and restored after. */
export function briefHint(hint: string): string {
  const MASK = '\u0000'
  const masked = hint.trim().replace(ABBREVIATIONS, (m) => m.replace(/\./g, MASK))
  const sentences = masked.split(/(?<=[.!?])\s+/)
  return sentences
    .slice(0, HINT_SENTENCES)
    .join(' ')
    .replace(new RegExp(MASK, 'g'), '.')
    .trim()
}

/** The first example under a `## Example(s)` heading in the field's long help,
 *  as one short line — or null when there is none or it is too long to be a
 *  quick illustration. Long help bodies are deliberately left out so the
 *  questionnaire isn't overwhelming. */
function briefExample(help: string | undefined): string | null {
  if (!help) return null
  const body = help.match(/##\s*Examples?\s*\n+([\s\S]*?)(?:\n##|$)/)?.[1]
  if (!body) return null
  const first = body
    .split('\n')
    .map((l) => l.replace(/^\s*[-*]\s+/, '').trim())
    .find(Boolean)
  if (!first || first.length > EXAMPLE_MAX) return null
  return first
}

/** The blank to fill in, shaped by the field kind. */
function answerSpace(field: FieldDef): string {
  const t = field.type
  switch (t.kind) {
    case 'radio':
      return t.options.map((o) => `- ☐ ${o.label}`).join('\n')
    case 'multiselect':
      return `_Tick all that apply:_\n\n${t.options.map((o) => `- ☐ ${o.label}`).join('\n')}`
    case 'eligibility-grid':
      return t.rows.map((r) => `- ☐ Pass — **${r.label}** — notes: ______`).join('\n')
    case 'list':
    case 'use-cases-table':
    case 'contacts-table':
    case 'qa-list':
      return '- \n- \n- '
    default:
      return '> _Your answer:_\n>\n> '
  }
}

/** The schema phases the questionnaire covers. */
const QUESTIONNAIRE_PHASES: readonly (string | undefined)[] = ['company', 'intake']
/** Intake sections the consultant assesses internally — never asked of the
 *  customer (Funding Eligibility is the AWS program checklist). */
const INTERNAL_SECTIONS: ReadonlySet<string> = new Set(['funding_eligibility'])

/** Whether a section belongs in the customer questionnaire. Shared with the
 *  public help page so the document and its links can never disagree on scope. */
export function inQuestionnaire(section: { key: string; phase?: string }): boolean {
  return QUESTIONNAIRE_PHASES.includes(section.phase) && !INTERNAL_SECTIONS.has(section.key)
}

const slug = (key: string) => key.replace(/_/g, '-')

/** Field keys that appear in more than one questionnaire section (e.g. each
 *  pillar's `maturity_score`) — their anchors carry the section to stay unique. */
const SHARED_FIELD_KEYS: ReadonlySet<string> = (() => {
  const counts = new Map<string, number>()
  for (const s of SECTIONS.filter(inQuestionnaire)) {
    for (const f of s.fields) counts.set(f.key, (counts.get(f.key) ?? 0) + 1)
  }
  return new Set([...counts].filter(([, n]) => n > 1).map(([k]) => k))
})()

/** Stable, readable per-question anchor on the public help page: the field key
 *  as a slug (`executive-sponsor`), prefixed with the section only when the key
 *  repeats across sections (`data-readiness-maturity-score`). */
export function questionAnchor(sectionKey: string, fieldKey: string): string {
  return SHARED_FIELD_KEYS.has(fieldKey) ? `${slug(sectionKey)}-${slug(fieldKey)}` : slug(fieldKey)
}

/** Anchor for a section heading on the help page (`organizational-readiness`). */
export function sectionAnchor(sectionKey: string): string {
  return slug(sectionKey)
}

/** The first questionnaires linked `#<section>.<field>`; map that legacy form to
 *  today's anchor so an already-emailed document still lands on its question. */
export function resolveLegacyAnchor(hash: string): string {
  const m = hash.match(/^([a-z0-9_]+)\.([a-z0-9_]+)$/)
  return m ? questionAnchor(m[1]!, m[2]!) : hash
}

/** One question block of the questionnaire. Answered fields show the captured
 *  answer (so the customer can confirm or correct it); unanswered ones show a
 *  brief hint, at most one short example, a link to the full help when the
 *  field has more than fits here, and a space to answer. */
/** Per-question help in the questionnaire: off, or on with an optional link
 *  base (the absolute URL of the public /questionnaire-help page). */
export interface QuestionnaireHelpOptions {
  /** Absolute URL of the public help page; adds a per-question "More help"
   *  link for fields whose help has more than fits in the document. */
  helpBaseUrl?: string
}

function questionBlock(
  sectionKey: string,
  field: FieldDef,
  value: unknown,
  help: QuestionnaireHelpOptions | null,
): string {
  const required = field.priority === 'required' ? ' _(required)_' : ''
  const head = `**${field.label}**${required}`
  const answered = formatField(field, value)
  if (answered !== null) return `${head}\n\n${answered}`
  const lines: string[] = []
  if (help) {
    lines.push(`_${briefHint(field.hint)}_`)
    const example = briefExample(field.help)
    if (example) lines.push(`_Example:_ ${example}`)
    if (help.helpBaseUrl && field.help) {
      lines.push(
        `[More help and examples](${help.helpBaseUrl}#${questionAnchor(sectionKey, field.key)})`,
      )
    }
  }
  lines.push(answerSpace(field))
  return `${head}\n\n${lines.join('\n\n')}`
}

/** The intake as a questionnaire to email the customer: every intake section
 *  and question in website order, answered ones pre-filled, unanswered ones
 *  with an answer space. `help` (null = none) adds a brief hint, at most one
 *  short example, and optionally a link to the full help per question. */
export function buildIntakeQuestionnaireMarkdown(
  state: AssessmentState,
  help: QuestionnaireHelpOptions | null = null,
): string {
  const parts: string[] = [
    '_Please answer the questions below. Questions we have already answered are pre-filled — correct anything that is wrong. Leave a question blank if it does not apply._',
  ]
  for (const section of SECTIONS) {
    if (!inQuestionnaire(section)) continue
    const captured = (state.captured_fields?.[section.key] ?? {}) as Record<string, unknown>
    const heading = section.number > 0 ? `${section.number}. ${section.title}` : section.title
    const blocks = section.fields.map((field) =>
      questionBlock(section.key, field, captured[field.key], help),
    )
    parts.push(`# ${heading}\n\n${blocks.join('\n\n')}`)
  }
  return parts.join('\n\n')
}

export function buildIntakeMarkdown(state: AssessmentState): string {
  const parts: string[] = []
  for (const section of SECTIONS) {
    // The company-basics prerequisite exports too — it's part of the intake
    // story even though it's edited on the standalone profile page.
    if (section.phase !== 'intake' && section.phase !== 'company') continue
    const captured = (state.captured_fields?.[section.key] ?? {}) as Record<string, unknown>
    const blocks: string[] = []
    for (const field of section.fields) {
      const formatted = formatField(field, captured[field.key])
      if (formatted === null) continue
      blocks.push(`**${field.label}**\n\n${formatted}`)
    }
    if (blocks.length === 0) continue
    const heading = section.number > 0 ? `${section.number}. ${section.title}` : section.title
    parts.push(`# ${heading}\n\n${blocks.join('\n\n')}`)
  }
  return parts.length ? parts.join('\n\n') : '_No intake answers captured yet._'
}
